import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  Input,
  Form,
  Space,
  message,
  Tooltip,
  Select,
} from 'antd';
import { SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';

const CATEGORIES = [
  'Automobile',
  'Immobilier',
  'Tourisme',
  'Santé',
  'Enseignement',
  'Commerces',
  'Bâtiment',
  'Artisanat',
  'Multi-secteurs',
  'Services professionnels',
];

const { TextArea } = Input;

export default function EditProductDrawer({ open, onClose, product, onSaved }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    if (product && open) {
      form.setFieldsValue({
        slug: product.slug,
        name: product.name,
        category: product.category,
        description: product.description,
        long_description: product.long_description,
        features: Array.isArray(product.features)
          ? product.features.join('\n')
          : product.features || '',
      });
    }
  }, [product, open, form]);

  const handleSave = async values => {
    setLoading(true);
    try {
      const token = localStorage.getItem('prae.jwt');
      const features = values.features
        ? values.features.split('\n').filter(f => f.trim())
        : [];

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/packaged-products/${product.slug}`, // toujours l'ancien slug en URL
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ ...values, features }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      message.success('Produit mis à jour !');
      onSaved();
      onClose();
    } catch (err) {
      message.error(`Erreur : ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAI = async () => {
    if (!product) return;
    setAiLoading(true);
    try {
      const token = localStorage.getItem('prae.jwt');
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/packaged-products/generate-ai`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            filters: {
              depts: product.filter_departments || [],
              communes: product.filter_communes || [],
              communesInsee: product.filter_communes_insee || [],
              naf: product.filter_naf || [],
              emailOnly: product.filter_email_only || false,
            },
            targetCount: product.establishment_count,
            activitySummary: (product.sector_labels || []).map(l => ({
              label: l,
            })),
          }),
        }
      );

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const generated = await res.json();

      form.setFieldsValue({
        name: generated.name,
        category: generated.category,
        description: generated.description,
        long_description: generated.long_description,
        features: Array.isArray(generated.features)
          ? generated.features.join('\n')
          : generated.features,
      });
      message.success("Champs générés par l'IA !");
    } catch (err) {
      message.error(`Erreur IA : ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <Drawer
      title={
        <span>
          Modifier le produit{' '}
          <span
            style={{ fontSize: 12, color: '#888', fontFamily: 'monospace' }}
          >
            {product?.slug}
          </span>
        </span>
      }
      placement="right"
      onClose={onClose}
      open={open}
      width={640}
      zIndex={1200}
      footer={
        <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Tooltip title="Régénère tous les champs texte à partir des critères du produit">
            <Button
              icon={<ThunderboltOutlined />}
              onClick={handleGenerateAI}
              loading={aiLoading}
              style={{ borderColor: '#7c3aed', color: '#7c3aed' }}
            >
              Régénérer avec l'IA
            </Button>
          </Tooltip>
          <Space>
            <Button onClick={onClose}>Annuler</Button>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={() => form.submit()}
              loading={loading}
            >
              Enregistrer
            </Button>
          </Space>
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleSave}>
        <Form.Item
          label="Slug (URL)"
          name="slug"
          rules={[
            { required: true },
            {
              pattern: /^[a-z0-9-]+$/,
              message: 'Minuscules, chiffres et tirets uniquement',
            },
          ]}
          extra="Modifier le slug changera l'URL du produit sur le site"
        >
          <Input style={{ fontFamily: 'monospace' }} />
        </Form.Item>

        <Form.Item
          label="Nom du produit"
          name="name"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Catégorie" name="category">
          <Select
            options={CATEGORIES.map(c => ({ value: c, label: c }))}
            placeholder="Sélectionner une catégorie"
            allowClear
          />
        </Form.Item>

        <Form.Item
          label="Description courte"
          name="description"
          rules={[{ required: true }]}
        >
          <TextArea rows={2} />
        </Form.Item>

        <Form.Item
          label="Description détaillée (Markdown)"
          name="long_description"
        >
          <TextArea rows={10} />
        </Form.Item>

        <Form.Item
          label="Caractéristiques"
          name="features"
          extra="Une caractéristique par ligne"
        >
          <TextArea rows={6} />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
